import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { alpha, Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { JobState, OrderState } from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';

import { Job } from '~hooks/useJob/models';
import { Order } from '~hooks/useOrders/models';
import { useStores } from '~store';
import { formatDuration } from '~utils/dateTime';
import formatNumber from '~utils/formatNumber';
import { parseFiniteNumber } from '~utils/utilFunctions';

const T_PREFIX = 'dispatch.order.metrics';
const EMPTY = '—';

const cardSx = (theme: Theme) => ({
  border: `1px solid ${theme.brandV2.colors.treadGray7}`,
  borderRadius: 1,
  backgroundColor: theme.semantic.surfaceRaised,
  p: 2.5,
});

const sectionTitleSx = (theme: Theme) => ({
  fontSize: 14,
  fontWeight: 600,
  color: theme.brandV2.colors.treadBlack,
  m: 0,
  mb: 1.75,
});

const eyebrowSx = (theme: Theme) => ({
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: theme.brandV2.colors.treadGray3,
  mb: 0.5,
});

const bigNumSx = (theme: Theme) => ({
  fontSize: 28,
  fontWeight: 700,
  letterSpacing: '-0.02em',
  fontVariantNumeric: 'tabular-nums',
  color: theme.brandV2.colors.treadBlack,
  lineHeight: 1.05,
  whiteSpace: 'nowrap',
});

const unitSx = (theme: Theme) => ({
  fontSize: 14,
  fontWeight: 500,
  color: theme.brandV2.colors.treadGray3,
  ml: 0.5,
});

const onsiteHeaderSx = (theme: Theme) => ({
  fontSize: 12,
  fontWeight: 500,
  color: theme.brandV2.colors.treadGray3,
});

interface OrderMetricsTabProps {
  order: Order;
}

function isPresent(value: unknown): value is number {
  return value !== null && value !== undefined && !Number.isNaN(value);
}

function formatTime(timestamp: Dayjs | null): string {
  if (!timestamp) return EMPTY;
  return dayjs.tz(timestamp).format('h:mm A');
}

function formatMinutes(minutes: number | null | undefined): string {
  if (!isPresent(minutes)) return EMPTY;
  return `${Math.round(minutes)} ${t(`${T_PREFIX}.min`)}`;
}

function formatHourMinutes(totalMinutes: number | null | undefined): string {
  if (!isPresent(totalMinutes)) return EMPTY;
  const minutes = Math.max(0, Math.round(totalMinutes));
  // formatDuration returns '' when every component is zero — keep the dash
  // so the cell never goes blank.
  return formatDuration(minutes * 60) || EMPTY;
}

function formatElapsedAgo(timestamp: Dayjs | null): string {
  if (!timestamp) return EMPTY;
  const diffMinutes = dayjs().diff(timestamp, 'minute');
  if (diffMinutes < 1) return t(`${T_PREFIX}.elapsed_less_than_1m`);
  return t(`${T_PREFIX}.elapsed_ago`, { elapsed: formatHourMinutes(diffMinutes) });
}

interface DeliveryData {
  shippedQty: number;
  deliveredQty: number;
  inTransitQty: number;
  orderQty: number | null;
  uomMismatch: boolean;
  uomName: string;
  loadsDelivered: number;
  loadsInTransit: number;
  totalLoads: number;
  barDenominator: number;
}

function computeDeliveryData(order: Order): DeliveryData {
  const summary = order.orderSummary;

  const materialGrossDelivered = summary?.materialGrossDeliveredQuantity ?? 0;
  const totalTicketsQty = summary?.totalTicketsQuantity ?? 0;
  const shippedQty = totalTicketsQty;
  const deliveredQty = materialGrossDelivered;
  const inTransitQty = Math.max(0, totalTicketsQty - materialGrossDelivered);

  const rawQty = order.quantity;
  const orderQty: number | null =
    rawQty === null || rawQty === undefined ? null : Number(rawQty);
  const orderUom = order.unitOfMeasure?.id ?? null;
  const shippedUom = summary?.materialGrossUnitOfMeasure?.id ?? null;
  const uomMismatch = orderUom !== null && shippedUom !== null && orderUom !== shippedUom;
  const uomName = shippedUom ?? orderUom ?? '';

  const loadsDelivered = summary?.deliveredLoadsCount ?? 0;
  const totalTicketsCount = summary?.totalTicketsCount ?? 0;
  const loadsInTransit = Math.max(0, totalTicketsCount - loadsDelivered);
  const totalLoads = totalTicketsCount;

  const barDenominator =
    isPresent(orderQty) && orderQty > 0 && !uomMismatch ? orderQty : shippedQty;

  return {
    shippedQty,
    deliveredQty,
    inTransitQty,
    orderQty,
    uomMismatch,
    uomName,
    loadsDelivered,
    loadsInTransit,
    totalLoads,
    barDenominator,
  };
}

const UOM_SINGULAR_KEY: Record<string, string> = {
  Bag: 'uom_bag',
  Barrel: 'uom_barrel',
  Bushel: 'uom_bushel',
  CubicMeter: 'uom_cubic_meter',
  Each: 'uom_each',
  Foot: 'uom_foot',
  Gallon: 'uom_gallon',
  Hour: 'uom_hour',
  Kilometer: 'uom_kilometer',
  Liter: 'uom_liter',
  Load: 'uom_load',
  Meter: 'uom_meter',
  Mile: 'uom_mile',
  Pallet: 'uom_pallet',
  Pound: 'uom_pound',
  Ton: 'uom_ton',
  Tonne: 'uom_tonne',
  Yard: 'uom_yard',
};

const UOM_PLURAL_KEY: Record<string, string> = {
  Bag: 'uom_bags',
  Barrel: 'uom_barrels',
  Bushel: 'uom_bushels',
  CubicMeter: 'uom_cubic_meters',
  Each: 'uom_eaches',
  Foot: 'uom_feet',
  Gallon: 'uom_gallons',
  Hour: 'uom_hours',
  Kilometer: 'uom_kilometers',
  Liter: 'uom_liters',
  Load: 'uom_loads',
  Meter: 'uom_meters',
  Mile: 'uom_miles',
  Pallet: 'uom_pallets',
  Pound: 'uom_pounds',
  Ton: 'uom_tons',
  Tonne: 'uom_tonnes',
  Yard: 'uom_yards',
};

function pluralizeUom(uom: string, count: number): string {
  if (!uom) return '';
  if (count === 1) {
    const singularKey = UOM_SINGULAR_KEY[uom];
    return singularKey ? t(`${T_PREFIX}.${singularKey}`) : uom;
  }
  const pluralKey = UOM_PLURAL_KEY[uom];
  return pluralKey ? t(`${T_PREFIX}.${pluralKey}`) : uom;
}

interface IdleSummary {
  avgIdlePerCycleMin: number | null;
  idlePctOfCycle: number | null;
  totalIdleMinutes: number | null;
  totalWorkMinutes: number | null;
}

function computeIdleSummary(order: Order, jobs: Job[]): IdleSummary {
  const summary = order.orderSummary;
  const totalIdleSeconds = summary?.idleTimeSeconds ?? 0;
  const totalIdleMinutes = totalIdleSeconds > 0 ? totalIdleSeconds / 60 : null;

  const deliveredLoads = summary?.deliveredLoadsCount ?? 0;
  const avgIdlePerCycleMin =
    totalIdleMinutes !== null && deliveredLoads > 0
      ? totalIdleMinutes / deliveredLoads
      : null;

  const cycleAvg = order.loadCycleAvg;
  const idlePctOfCycle =
    avgIdlePerCycleMin !== null && cycleAvg > 0
      ? (avgIdlePerCycleMin / cycleAvg) * 100
      : null;

  const totalWorkMinutes = jobs.reduce<number | null>((acc, job) => {
    const work = job.jobSummary?.workTimeMinutes;
    if (!isPresent(work)) return acc;
    return (acc ?? 0) + work;
  }, null);

  return {
    avgIdlePerCycleMin,
    idlePctOfCycle,
    totalIdleMinutes,
    totalWorkMinutes,
  };
}

interface StartTimes {
  firstReachedPickupAt: Dayjs | null;
  firstLeftPickupAt: Dayjs | null;
  firstDroppedOffAt: Dayjs | null;
  lastGeofenceExitAt: Dayjs | null;
  scheduledStartAt: Dayjs | null;
}

const TERMINAL_ORDER_STATES: ReadonlySet<OrderState> = new Set([
  OrderState.COMPLETED,
  OrderState.CANCELED,
]);

function computeStartTimes(order: Order, jobs: Job[]): StartTimes {
  const summary = order.orderSummary;
  const arrivedPickupAt = summary?.firstLoadArrivedPickupAt ?? null;
  const leftPickupAt = summary?.firstLoadToDropoffAt ?? null;
  const firstDroppedOffAt = summary?.firstLoadUnloadedAt ?? null;
  const scheduledStartAt = order.jobStartAt ?? null;

  // Each of the three cells is an earliest-across-all-loads value and can come from a
  // different truck, so a pickup time can land after the first dropoff. Blank the
  // offending cell rather than render an impossible sequence.
  const isAfterFirstDropoff = (time: Dayjs | null) =>
    Boolean(firstDroppedOffAt && time?.isAfter(firstDroppedOffAt));
  const reachedPickupOutrunsDropoff = isAfterFirstDropoff(arrivedPickupAt);
  const leftPickupOutrunsDropoff = isAfterFirstDropoff(leftPickupAt);

  const firstReachedPickupAt = reachedPickupOutrunsDropoff ? null : arrivedPickupAt;
  // The two pickup cells populate together, so "left pickup" never stands alone.
  const firstLeftPickupAt =
    reachedPickupOutrunsDropoff || leftPickupOutrunsDropoff ? null : leftPickupAt;

  const lastGeofenceExitAt = jobs.reduce<Dayjs | null>((acc, job) => {
    const exit = job.jobSummary?.lastGeofenceExitAt ?? null;
    if (!exit) return acc;
    if (!acc) return exit;
    return exit.isAfter(acc) ? exit : acc;
  }, null);

  return {
    firstReachedPickupAt,
    firstLeftPickupAt,
    firstDroppedOffAt,
    lastGeofenceExitAt,
    scheduledStartAt,
  };
}

interface CycleStageCounts {
  toPickup: number;
  atPickup: number;
  toDropoff: number;
  atDropoff: number;
  inCycleTotal: number;
  completed: number;
}

const IN_CYCLE_STATES: ReadonlySet<JobState> = new Set([
  JobState.TO_PICKUP,
  JobState.ARRIVED_PICKUP,
  JobState.LOADED,
  JobState.TO_DROPOFF,
  JobState.ARRIVED_DROPOFF,
  JobState.UNLOADED,
]);

const AT_PICKUP_STATES: ReadonlySet<JobState> = new Set([
  JobState.ARRIVED_PICKUP,
  JobState.LOADED,
]);
const AT_DROPOFF_STATES: ReadonlySet<JobState> = new Set([
  JobState.ARRIVED_DROPOFF,
  JobState.UNLOADED,
]);

function computeCycleStageCounts(jobs: Job[]): CycleStageCounts {
  const countIn = (states: ReadonlySet<JobState>) =>
    jobs.filter((j) => states.has(j.status)).length;
  return {
    toPickup: jobs.filter((j) => j.status === JobState.TO_PICKUP).length,
    atPickup: countIn(AT_PICKUP_STATES),
    toDropoff: jobs.filter((j) => j.status === JobState.TO_DROPOFF).length,
    atDropoff: countIn(AT_DROPOFF_STATES),
    inCycleTotal: countIn(IN_CYCLE_STATES),
    completed: jobs.filter((j) => j.status === JobState.COMPLETED).length,
  };
}

function LoadPerformanceCard({ order, idle }: { order: Order; idle: IdleSummary }) {
  const theme = useTheme();
  const cycleAvg = order.loadCycleAvg;

  const cycleHasValue = isPresent(cycleAvg) && cycleAvg > 0;
  const cycleMain = cycleHasValue ? Math.round(cycleAvg) : null;

  const idleSubline =
    idle.avgIdlePerCycleMin !== null && idle.idlePctOfCycle !== null ? (
      <>
        {t(`${T_PREFIX}.idle_label`)}{' '}
        <Box
          component="b"
          sx={{ fontWeight: 700, color: theme.brandV2.colors.treadBlack }}
        >
          {Math.round(idle.avgIdlePerCycleMin)} {t(`${T_PREFIX}.min`)}
        </Box>{' '}
        ·{' '}
        <Box
          component="b"
          sx={{ fontWeight: 700, color: theme.brandV2.colors.treadBlack }}
        >
          {Math.round(idle.idlePctOfCycle)}%
        </Box>
      </>
    ) : (
      EMPTY
    );

  return (
    <Box sx={cardSx}>
      <Typography sx={sectionTitleSx}>{t(`${T_PREFIX}.load_performance`)}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 3 }}>
        <Stat
          eyebrow={t(`${T_PREFIX}.avg_load_cycle`)}
          value={cycleMain !== null ? `${cycleMain}` : EMPTY}
          unit={cycleMain !== null ? t(`${T_PREFIX}.min`) : undefined}
          sub={
            <Box sx={{ fontSize: 13, color: theme.brandV2.colors.treadGray1, mt: 1.25 }}>
              {idleSubline}
            </Box>
          }
          divider
        />
        <Stat
          eyebrow={t(`${T_PREFIX}.total_idle`)}
          value={formatHourMinutes(idle.totalIdleMinutes)}
          divider
        />
        <Stat
          eyebrow={t(`${T_PREFIX}.total_work_time`)}
          value={formatHourMinutes(idle.totalWorkMinutes)}
        />
      </Box>
    </Box>
  );
}

function Stat({
  eyebrow,
  value,
  unit,
  sub,
  divider,
}: {
  eyebrow: string;
  value: string;
  unit?: string;
  sub?: React.ReactNode;
  divider?: boolean;
}) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        pr: divider ? 2.5 : 0,
        borderRight: divider ? `1px solid ${theme.brandV2.colors.treadGray7}` : 'none',
      }}
    >
      <Typography sx={eyebrowSx}>{eyebrow}</Typography>
      <Box sx={{ display: 'inline-flex', alignItems: 'baseline' }}>
        <Box sx={bigNumSx}>{value}</Box>
        {unit ? <Box sx={unitSx}>{unit}</Box> : null}
      </Box>
      {sub}
    </Box>
  );
}

function SingleMaterialShippedCard({ order }: { order: Order }) {
  const theme = useTheme();
  const data = useMemo(() => computeDeliveryData(order), [order]);
  const uomLabel = useMemo(
    () => pluralizeUom(data.uomName, data.orderQty ?? data.shippedQty ?? 0),
    [data],
  );
  const uomLower = (uomLabel || '').toLowerCase();

  const deliveredPctRaw =
    data.barDenominator > 0 ? ((data.deliveredQty ?? 0) / data.barDenominator) * 100 : 0;
  const inTransitPctRaw =
    data.barDenominator > 0 ? ((data.inTransitQty ?? 0) / data.barDenominator) * 100 : 0;
  const shippedPctTotal = deliveredPctRaw + inTransitPctRaw;
  const shippedPctScale = shippedPctTotal > 100 ? 100 / shippedPctTotal : 1;
  const deliveredPct = deliveredPctRaw * shippedPctScale;
  const inTransitPct = inTransitPctRaw * shippedPctScale;
  const remainingPct = Math.max(0, 100 - deliveredPct - inTransitPct);

  const hasOrderQty = isPresent(data.orderQty) && !data.uomMismatch;
  const remainingQty = hasOrderQty
    ? Math.max(0, (data.orderQty ?? 0) - (data.shippedQty ?? 0))
    : null;

  const pctOfOrdered =
    hasOrderQty && (data.orderQty ?? 0) > 0
      ? (data.shippedQty / (data.orderQty ?? 1)) * 100
      : null;

  const segments = [
    {
      label: t(`${T_PREFIX}.delivered`),
      value: data.deliveredQty,
      loads: data.loadsDelivered,
      pct: deliveredPct,
      color: theme.brandV2.colors.treadGreen,
    },
    {
      label: t(`${T_PREFIX}.en_route`),
      value: data.inTransitQty,
      loads: data.loadsInTransit,
      pct: inTransitPct,
      color: theme.brandV2.colors.treadBlue2,
    },
    ...(hasOrderQty
      ? [
          {
            label: t(`${T_PREFIX}.remaining`),
            value: remainingQty,
            loads: null as number | null,
            pct: remainingPct,
            color: theme.brandV2.colors.treadGray7,
          },
        ]
      : []),
  ];

  return (
    <Box sx={cardSx}>
      <Typography sx={sectionTitleSx}>{t(`${T_PREFIX}.material_shipped`)}</Typography>
      <Box sx={{ display: 'inline-flex', alignItems: 'baseline' }}>
        <Box sx={bigNumSx}>{(data.shippedQty ?? 0).toFixed(2)}</Box>
        <Box sx={unitSx}>
          {hasOrderQty ? `/ ${(data.orderQty ?? 0).toFixed(0)} ${uomLower}` : uomLower}
          {pctOfOrdered !== null ? ` · ${pctOfOrdered.toFixed(1)}%` : ''}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          borderRadius: '999px',
          overflow: 'hidden',
          height: 10,
          mt: 1.5,
          backgroundColor: theme.brandV2.colors.treadGray9,
        }}
      >
        {segments.map((seg) =>
          seg.pct > 0 ? (
            <Box
              key={seg.label}
              sx={{
                width: `${seg.pct}%`,
                backgroundColor: seg.color,
                transition: 'width 0.3s ease',
              }}
            />
          ) : null,
        )}
      </Box>
      <Box sx={{ mt: 1.75, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {segments.map((seg) => (
          <Box key={seg.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: seg.color,
                flexShrink: 0,
              }}
            />
            <Typography sx={{ fontSize: 13, color: theme.brandV2.colors.treadGray3 }}>
              {seg.label}
            </Typography>
            <Typography
              sx={{
                ml: 'auto',
                fontSize: 13,
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
                color: theme.brandV2.colors.treadBlack,
              }}
            >
              {isPresent(seg.value) ? seg.value.toFixed(2) : ''} {uomLower}
              {isPresent(seg.loads)
                ? ` · ${t(`${T_PREFIX}.loads_count`, { count: seg.loads })}`
                : ''}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

interface MaterialDeliveryRow {
  key: string;
  materialName: string | null;
  uomName: string;
  orderedQty: number | null;
  deliveredQty: number;
}

interface OrderedMaterialLine {
  materialName: string | null;
  uomId: string;
  quantity: number | null;
}

// An order's own material and quantity sit beside the additive material_totals list, so
// the primary material is only a separate line when no total already covers it.
function getOrderedMaterialLines(order: Order): OrderedMaterialLine[] {
  const lines = order.materialTotals.map((materialTotal) => ({
    materialName: materialTotal.material?.name ?? null,
    uomId: materialTotal.unitOfMeasure?.id ?? '',
    quantity: parseFiniteNumber(materialTotal.totalQuantity),
  }));

  const primaryQuantity = parseFiniteNumber(order.quantity);
  const primaryHasMaterialOrQuantity =
    Boolean(order.material) || primaryQuantity !== null;
  const primaryIsInMaterialTotals =
    order.material !== null &&
    order.materialTotals.some(
      (materialTotal) =>
        materialTotal.material?.id === order.material?.id &&
        materialTotal.unitOfMeasure?.id === order.unitOfMeasure?.id,
    );

  if (primaryHasMaterialOrQuantity && !primaryIsInMaterialTotals) {
    lines.unshift({
      materialName: order.material?.name ?? null,
      uomId: order.unitOfMeasure?.id ?? '',
      quantity: primaryQuantity,
    });
  }

  return lines;
}

function computeMaterialDeliveryRows(order: Order): MaterialDeliveryRow[] {
  const rowsByKey = new Map<string, MaterialDeliveryRow>();
  const rowFor = (materialName: string | null, uomName: string) => {
    const key = `${materialName ?? ''}::${uomName}`;
    const existing = rowsByKey.get(key);
    if (existing) return existing;

    const created = { key, materialName, uomName, orderedQty: null, deliveredQty: 0 };
    rowsByKey.set(key, created);
    return created;
  };

  for (const line of getOrderedMaterialLines(order)) {
    const row = rowFor(line.materialName, line.uomId);
    if (line.quantity !== null) row.orderedQty = (row.orderedQty ?? 0) + line.quantity;
  }

  // The API splits a material and unit into separate rows by ticketed state, so sum them.
  for (const delivered of order.orderSummary?.deliveredQuantities ?? []) {
    const row = rowFor(delivered.materialName, delivered.unitOfMeasure ?? '');
    row.deliveredQty += parseFiniteNumber(delivered.delivered) ?? 0;
  }

  return [...rowsByKey.values()].filter(
    (row) => row.orderedQty !== null || row.deliveredQty > 0,
  );
}

function MaterialDeliveryRowView({ row }: { row: MaterialDeliveryRow }) {
  const theme = useTheme();
  const orderedQty = row.orderedQty;
  const deliveredPct =
    orderedQty !== null && orderedQty > 0 ? (row.deliveredQty / orderedQty) * 100 : null;
  const uomLabel = pluralizeUom(
    row.uomName,
    orderedQty ?? row.deliveredQty,
  ).toLowerCase();

  return (
    <Box data-test-id="material-delivery-row">
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography
          sx={{ fontSize: 13, fontWeight: 600, color: theme.brandV2.colors.treadBlack }}
        >
          {row.materialName ?? t(`${T_PREFIX}.unspecified_material`)}
        </Typography>
        <Typography
          sx={{
            ml: 'auto',
            fontSize: 13,
            fontVariantNumeric: 'tabular-nums',
            color: theme.brandV2.colors.treadGray3,
          }}
        >
          {formatNumber(row.deliveredQty)}
          {orderedQty !== null ? ` / ${formatNumber(orderedQty)}` : ''} {uomLabel}
          {deliveredPct !== null ? ` · ${deliveredPct.toFixed(1)}%` : ''}
        </Typography>
      </Box>
      {deliveredPct !== null ? (
        <Box
          sx={{
            borderRadius: '999px',
            overflow: 'hidden',
            height: 8,
            mt: 0.75,
            backgroundColor: theme.brandV2.colors.treadGray9,
          }}
        >
          <Box
            sx={{
              width: `${Math.min(100, deliveredPct)}%`,
              height: '100%',
              backgroundColor: theme.brandV2.colors.treadGreen,
              transition: 'width 0.3s ease',
            }}
          />
        </Box>
      ) : null}
    </Box>
  );
}

function MultiMaterialShippedCard({ order }: { order: Order }) {
  const theme = useTheme();
  const rows = useMemo(() => computeMaterialDeliveryRows(order), [order]);
  const deliveredLoadsCount = order.orderSummary?.deliveredLoadsCount ?? 0;

  return (
    <Box sx={cardSx}>
      <Typography sx={sectionTitleSx}>{t(`${T_PREFIX}.material_shipped`)}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        {rows.map((row) => (
          <MaterialDeliveryRowView key={row.key} row={row} />
        ))}
      </Box>
      <Typography sx={{ mt: 1.75, fontSize: 13, color: theme.brandV2.colors.treadGray3 }}>
        {t(`${T_PREFIX}.loads_delivered`, { count: deliveredLoadsCount })}
      </Typography>
    </Box>
  );
}

function MaterialShippedCard({ order }: { order: Order }) {
  return getOrderedMaterialLines(order).length > 1 ? (
    <MultiMaterialShippedCard order={order} />
  ) : (
    <SingleMaterialShippedCard order={order} />
  );
}

function TodaysStartCard({ order, jobs }: { order: Order; jobs: Job[] }) {
  const theme = useTheme();
  const ARROW_URI_COLOR = encodeURIComponent(theme.brandV2.colors.treadGray5);
  const times = useMemo(() => computeStartTimes(order, jobs), [order, jobs]);
  const isTerminal = TERMINAL_ORDER_STATES.has(order.state);

  const pickupSiteName = order.pickUpWayPoints?.[0]?.site?.name ?? '';
  const dropoffSiteName = order.dropOffWayPoints?.[0]?.site?.name ?? '';

  const cells: Array<{
    testId: string;
    eyebrow: string;
    time: Dayjs | null;
    sub: React.ReactNode;
  }> = [
    {
      testId: 'start-time-first-truck-reached-pickup',
      eyebrow: t(`${T_PREFIX}.first_truck_reached_pickup`),
      time: times.firstReachedPickupAt,
      sub: times.scheduledStartAt ? (
        <>
          {t(`${T_PREFIX}.scheduled_start_at`, {
            time: formatTime(times.scheduledStartAt),
          })}
        </>
      ) : null,
    },
    {
      testId: 'start-time-first-truck-left-pickup',
      eyebrow: t(`${T_PREFIX}.first_truck_left_pickup`),
      time: times.firstLeftPickupAt,
      sub: isTerminal ? null : formatElapsedAgo(times.firstLeftPickupAt),
    },
    {
      testId: 'start-time-first-load-dropped-off',
      eyebrow: t(`${T_PREFIX}.first_load_dropped_off`),
      time: times.firstDroppedOffAt,
      sub: isTerminal
        ? times.lastGeofenceExitAt
          ? t(`${T_PREFIX}.final_geofence_exit_at`, {
              time: formatTime(times.lastGeofenceExitAt),
            })
          : null
        : formatElapsedAgo(times.firstDroppedOffAt),
    },
  ];

  const rows = [
    {
      kind: t(`${T_PREFIX}.pick_up`),
      siteName: pickupSiteName,
      perLoad: order.loadsPickupTimeMinutesAvg,
    },
    {
      kind: t(`${T_PREFIX}.drop_off`),
      siteName: dropoffSiteName,
      perLoad: order.loadsDropoffTimeMinutesAvg,
    },
  ];

  return (
    <Box sx={cardSx}>
      <Typography sx={sectionTitleSx}>{t(`${T_PREFIX}.today_start`)}</Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr auto 1fr',
          rowGap: 0.5,
          columnGap: 2.5,
          alignItems: 'center',
        }}
      >
        {cells.map((cell, idx) => (
          <Typography
            key={`eyebrow-${idx}`}
            data-test-id={`${cell.testId}-label`}
            sx={{ ...eyebrowSx(theme), gridRow: 1, gridColumn: idx * 2 + 1 }}
          >
            {cell.eyebrow}
          </Typography>
        ))}
        {cells.map((cell, idx) => (
          <Box
            key={`time-${idx}`}
            data-test-id={cell.testId}
            sx={{
              gridRow: 2,
              gridColumn: idx * 2 + 1,
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: '-0.01em',
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1.05,
              color: theme.brandV2.colors.treadBlack,
            }}
          >
            {formatTime(cell.time)}
          </Box>
        ))}
        {[0, 1].map((sepIdx) => (
          <Box
            key={`arrow-${sepIdx}`}
            aria-hidden
            sx={{
              gridRow: 2,
              gridColumn: sepIdx * 2 + 2,
              width: 14,
              height: 14,
              backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14'><path d='M3 2.5 L11 7 L3 11.5 Z' fill='${ARROW_URI_COLOR}'/></svg>")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
            }}
          />
        ))}
        {cells.map((cell, idx) => (
          <Typography
            key={`sub-${idx}`}
            sx={{
              gridRow: 3,
              gridColumn: idx * 2 + 1,
              fontSize: 11,
              color: theme.brandV2.colors.treadGray3,
              fontVariantNumeric: 'tabular-nums',
              '& b': { color: theme.brandV2.colors.treadGray3, fontWeight: 600 },
            }}
          >
            {cell.sub}
          </Typography>
        ))}
      </Box>

      <Box sx={{ mt: 3.5 }}>
        <Typography sx={{ ...sectionTitleSx(theme), mb: 1.25 }}>
          {t(`${T_PREFIX}.avg_time_on_site`)}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 96px',
            columnGap: 3,
            py: 1.25,
            borderBottom: `1px solid ${theme.brandV2.colors.treadGray7}`,
          }}
        >
          <Typography sx={onsiteHeaderSx}>{t(`${T_PREFIX}.site`)}</Typography>
          <OnsiteHeaderWithTooltip
            label={t(`${T_PREFIX}.per_load`)}
            tooltip={t(`${T_PREFIX}.per_load_tooltip`)}
          />
        </Box>
        {rows.map((row, idx) => (
          <Box
            key={row.kind}
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 96px',
              columnGap: 3,
              py: 1.75,
              borderBottom:
                idx < rows.length - 1
                  ? `1px solid ${theme.brandV2.colors.treadGray7}`
                  : 'none',
            }}
          >
            <Box>
              <Typography
                component="span"
                sx={{ fontSize: 14, color: theme.brandV2.colors.treadGray3, mr: 1 }}
              >
                {row.kind}
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: theme.brandV2.colors.treadBlack,
                }}
              >
                {row.siteName}
              </Typography>
            </Box>
            <OnsiteCell value={formatMinutes(row.perLoad)} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function OnsiteCell({ value }: { value: string }) {
  const theme = useTheme();

  return (
    <Typography
      sx={{
        fontSize: 16,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: '-0.01em',
        color: theme.brandV2.colors.treadBlack,
        textAlign: 'right',
      }}
    >
      {value}
    </Typography>
  );
}

function OnsiteHeaderWithTooltip({ label, tooltip }: { label: string; tooltip: string }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        ...onsiteHeaderSx(theme),
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        justifyContent: 'flex-end',
      }}
    >
      <span>{label}</span>
      <Tooltip title={tooltip} arrow placement="top">
        <InfoOutlined
          sx={{
            fontSize: 14,
            color: theme.brandV2.colors.treadGray3,
            cursor: 'help',
          }}
        />
      </Tooltip>
    </Box>
  );
}

const CYCLE_STAGES = [
  { angle: 0, labelKey: 'to_pickup', stateField: 'toPickup' as const },
  { angle: 90, labelKey: 'at_pickup', stateField: 'atPickup' as const },
  { angle: 180, labelKey: 'to_dropoff', stateField: 'toDropoff' as const },
  { angle: 270, labelKey: 'at_dropoff', stateField: 'atDropoff' as const },
];

const ORBIT_SIZE = 240;
const ORBIT_RADIUS = 88;
const ORBIT_CENTER = ORBIT_SIZE / 2;

function TrucksCycleCard({ order, jobs }: { order: Order; jobs: Job[] }) {
  const theme = useTheme();
  const ARROW_COLOR = theme.brandV2.colors.treadGray5;
  const NODE_ACTIVE_BG = alpha(theme.brandV2.colors.treadBlue2, 0.12);
  const COMPLETED_PILL_BG = alpha(theme.palette.success.main, 0.04);
  const stats = useMemo(() => computeCycleStageCounts(jobs), [jobs]);
  const totalJobsCount = order.orderSummary?.jobsCount ?? 0;
  const hasCompleted = stats.completed > 0;

  return (
    <Box sx={cardSx}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Typography sx={sectionTitleSx}>{t(`${T_PREFIX}.trucks`)}</Typography>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: hasCompleted
              ? COMPLETED_PILL_BG
              : theme.semantic.surfaceSunken,
            color: hasCompleted
              ? theme.palette.success.main
              : theme.brandV2.colors.treadGray1,
            px: 1.5,
            py: 0.5,
            borderRadius: 999,
            fontSize: 13,
          }}
        >
          {hasCompleted ? (
            <Box
              component="svg"
              viewBox="0 0 16 16"
              sx={{ width: 16, height: 16, flexShrink: 0 }}
              aria-hidden
              data-test-id="trucks-completed-checkmark"
            >
              <circle cx="8" cy="8" r="8" fill={theme.palette.success.main} />
              <path
                d="M4.5 8.2 L7 10.6 L11.6 5.8"
                fill="none"
                stroke={theme.palette.common.white}
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </Box>
          ) : null}
          <Typography component="span" sx={{ fontSize: 13, fontWeight: 500 }}>
            {t(`${T_PREFIX}.completed_count`, {
              done: stats.completed,
              total: totalJobsCount,
            })}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          position: 'relative',
          width: ORBIT_SIZE,
          height: ORBIT_SIZE,
          mx: 'auto',
          mt: 0.5,
        }}
      >
        <Box
          component="svg"
          viewBox={`0 0 ${ORBIT_SIZE} ${ORBIT_SIZE}`}
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
          aria-hidden
        >
          <defs>
            <marker
              id="cycle-arrow-head"
              viewBox="0 0 10 10"
              refX="6"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill={ARROW_COLOR} />
            </marker>
          </defs>
          <circle
            cx={ORBIT_CENTER}
            cy={ORBIT_CENTER}
            r={ORBIT_RADIUS}
            fill="none"
            stroke={theme.brandV2.colors.treadGray7}
            strokeWidth="1.5"
            strokeDasharray="3 5"
          />
          {[45, 135, 225, 315].map((a) => {
            const rad = ((a - 90) * Math.PI) / 180;
            const x = ORBIT_CENTER + ORBIT_RADIUS * Math.cos(rad);
            const y = ORBIT_CENTER + ORBIT_RADIUS * Math.sin(rad);
            const tx = -Math.sin(rad);
            const ty = Math.cos(rad);
            const x2 = x + tx * 0.1;
            const y2 = y + ty * 0.1;
            return (
              <line
                key={a}
                x1={x}
                y1={y}
                x2={x2}
                y2={y2}
                stroke={ARROW_COLOR}
                strokeWidth="1"
                markerEnd="url(#cycle-arrow-head)"
              />
            );
          })}
        </Box>

        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            width: 90,
          }}
        >
          <Box
            sx={{
              fontSize: 26,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1,
              color: theme.brandV2.colors.treadBlack,
            }}
          >
            {stats.inCycleTotal}
          </Box>
          <Box
            sx={{
              fontSize: 10,
              color: theme.brandV2.colors.treadGray3,
              mt: 0.5,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            {t(`${T_PREFIX}.in_cycle`)}
          </Box>
        </Box>

        {CYCLE_STAGES.map((stage) => {
          const rad = ((stage.angle - 90) * Math.PI) / 180;
          const x = ORBIT_CENTER + ORBIT_RADIUS * Math.cos(rad);
          const y = ORBIT_CENTER + ORBIT_RADIUS * Math.sin(rad);
          const count = stats[stage.stateField];
          const active = count > 0;
          return (
            <Box
              key={stage.labelKey}
              data-test-id={`cycle-node-${stage.stateField}`}
              sx={{
                position: 'absolute',
                left: x,
                top: y,
                transform: 'translate(-50%, -50%)',
                width: 84,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.25,
              }}
            >
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: 999,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1,
                  backgroundColor: active ? NODE_ACTIVE_BG : theme.semantic.surfaceRaised,
                  border: `1.5px solid ${
                    active
                      ? theme.brandV2.colors.treadBlue2
                      : theme.brandV2.colors.treadGray7
                  }`,
                  color: active
                    ? theme.brandV2.colors.treadBlue2
                    : theme.brandV2.colors.treadGray3,
                }}
              >
                {count}
              </Box>
              <Box
                sx={{
                  fontSize: 10,
                  lineHeight: 1.2,
                  mt: 0.5,
                  color: active
                    ? theme.brandV2.colors.treadBlack
                    : theme.brandV2.colors.treadGray3,
                  fontWeight: active ? 600 : 400,
                }}
              >
                {t(`${T_PREFIX}.${stage.labelKey}`)}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

const OrderMetricsTab = observer(({ order }: OrderMetricsTabProps) => {
  const { ordersDispatchStore } = useStores();
  const jobs: Job[] = ordersDispatchStore.orderJobs.get(order.id) ?? [];

  const idleSummary = useMemo(() => computeIdleSummary(order, jobs), [order, jobs]);

  return (
    <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
        <LoadPerformanceCard order={order} idle={idleSummary} />
        <MaterialShippedCard order={order} />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 1 }}>
        <TodaysStartCard order={order} jobs={jobs} />
        <TrucksCycleCard order={order} jobs={jobs} />
      </Box>
    </Box>
  );
});

export default OrderMetricsTab;
